export enum StaffPermissionRole {
  ADMIN = 'Admin',
  MANAGER = 'Manager',
  PROVIDER = 'Provider',
  HYGIENIST = 'Hygienist',
  DENTAL_ASSISTANT = 'Dental Assistant',
  FRONT_DESK = 'Front Desk',
  ACCOUNTING = 'Accounting',
}

export enum StaffScope {
  BILLING_READ = 'billing:read',
  BILLING_WRITE = 'billing:write',
  MEMBERSHIP_READ = 'membership:read',
  MEMBERSHIP_WRITE = 'membership:write',
  CLINICAL_READ = 'clinical:read',
  CLINICAL_WRITE = 'clinical:write',
  CLAIMS_READ = 'claims:read',
  CLAIMS_WRITE = 'claims:write',
  PATIENTS_READ = 'patients:read',
  PATIENTS_WRITE = 'patients:write',
  REPORTING_READ = 'reporting:read',
  SETTINGS_READ = 'settings:read',
  SETTINGS_WRITE = 'settings:write',
  SCHEDULING_READ = 'scheduling:read',
  SCHEDULING_WRITE = 'scheduling:write',
  STAFF_READ = 'staff:read',
  STAFF_WRITE = 'staff:write',
  TREATMENT_READ = 'treatment:read',
  TREATMENT_WRITE = 'treatment:write',
  DASHBOARD_READ = 'dashboard:read',
  DASHBOARD_WRITE = 'dashboard:write',
  MARKETING_READ = 'marketing:read',
  MARKETING_WRITE = 'marketing:write',
  MESSAGING_READ = 'messaging:read',
  MESSAGING_WRITE = 'messaging:write',
  NOTES_READ = 'notes:read',
  NOTES_WRITE = 'notes:write',
  FILES_READ = 'files:read',
  FILES_WRITE = 'files:write',
  FORMS_READ = 'forms:read',
  FORMS_WRITE = 'forms:write',
  IMAGES_READ = 'images:read',
  IMAGES_WRITE = 'images:write',
  LAB_READ = 'lab:read',
  LAB_WRITE = 'lab:write',
  NOTIFICATIONS_READ = 'notifications:read',
  NOTIFICATIONS_WRITE = 'notifications:write',
}

export const STAFF_SCOPE_LIST: readonly StaffScope[] = [
  StaffScope.BILLING_READ,
  StaffScope.BILLING_WRITE,
  StaffScope.MEMBERSHIP_READ,
  StaffScope.MEMBERSHIP_WRITE,
  StaffScope.CLINICAL_READ,
  StaffScope.CLINICAL_WRITE,
  StaffScope.CLAIMS_READ,
  StaffScope.CLAIMS_WRITE,
  StaffScope.PATIENTS_READ,
  StaffScope.PATIENTS_WRITE,
  StaffScope.REPORTING_READ,
  StaffScope.SETTINGS_READ,
  StaffScope.SETTINGS_WRITE,
  StaffScope.SCHEDULING_READ,
  StaffScope.SCHEDULING_WRITE,
  StaffScope.STAFF_READ,
  StaffScope.STAFF_WRITE,
  StaffScope.TREATMENT_READ,
  StaffScope.TREATMENT_WRITE,
  StaffScope.DASHBOARD_READ,
  StaffScope.DASHBOARD_WRITE,
  StaffScope.MARKETING_READ,
  StaffScope.MARKETING_WRITE,
  StaffScope.MESSAGING_READ,
  StaffScope.MESSAGING_WRITE,
  StaffScope.NOTES_READ,
  StaffScope.NOTES_WRITE,
  StaffScope.FILES_READ,
  StaffScope.FILES_WRITE,
  StaffScope.FORMS_READ,
  StaffScope.FORMS_WRITE,
  StaffScope.IMAGES_READ,
  StaffScope.IMAGES_WRITE,
  StaffScope.LAB_READ,
  StaffScope.LAB_WRITE,
  StaffScope.NOTIFICATIONS_READ,
  StaffScope.NOTIFICATIONS_WRITE,
]

export enum PermissionModule {
  BILLING_PAYMENTS = 'Billing & payments',
  MEMBERSHIP = 'Membership',
  CLINICAL_CHARTS = 'Clinical charts',
  INSURANCE_CLAIMS = 'Insurance claims',
  PATIENT_RECORDS = 'Patient records',
  REPORTING = 'Reporting',
  SETTINGS = 'Settings',
  SCHEDULING = 'Scheduling',
  STAFF_MANAGEMENT = 'Staff management',
  TREATMENT_PLANS = 'Treatment plans',
  DASHBOARD = 'Dashboard',
  MARKETING = 'Marketing',
  MESSAGE = 'Message',
  NOTES = 'Notes',
  FILES_LETTERS = 'Files & letters',
  FORM_BUILDER = 'Form builder',
  IMAGE_VIEWER = 'Image viewer',
  LAB = 'Lab',
  NOTIFICATIONS = 'Notifications',
}

export const PERMISSION_MODULE_ROW_ORDER: readonly PermissionModule[] = [
  PermissionModule.BILLING_PAYMENTS,
  PermissionModule.MEMBERSHIP,
  PermissionModule.CLINICAL_CHARTS,
  PermissionModule.INSURANCE_CLAIMS,
  PermissionModule.PATIENT_RECORDS,
  PermissionModule.REPORTING,
  PermissionModule.SETTINGS,
  PermissionModule.SCHEDULING,
  PermissionModule.STAFF_MANAGEMENT,
  PermissionModule.TREATMENT_PLANS,
  PermissionModule.DASHBOARD,
  PermissionModule.MARKETING,
  PermissionModule.MESSAGE,
  PermissionModule.NOTES,
  PermissionModule.FILES_LETTERS,
  PermissionModule.FORM_BUILDER,
  PermissionModule.IMAGE_VIEWER,
  PermissionModule.LAB,
  PermissionModule.NOTIFICATIONS,
]

export const PERMISSION_MODULE_SCOPES: Readonly<
  Record<PermissionModule, readonly StaffScope[]>
> = {
  [PermissionModule.BILLING_PAYMENTS]: [
    StaffScope.BILLING_READ,
    StaffScope.BILLING_WRITE,
  ],
  [PermissionModule.MEMBERSHIP]: [
    StaffScope.MEMBERSHIP_READ,
    StaffScope.MEMBERSHIP_WRITE,
  ],
  [PermissionModule.CLINICAL_CHARTS]: [
    StaffScope.CLINICAL_READ,
    StaffScope.CLINICAL_WRITE,
  ],
  [PermissionModule.INSURANCE_CLAIMS]: [
    StaffScope.CLAIMS_READ,
    StaffScope.CLAIMS_WRITE,
  ],
  [PermissionModule.PATIENT_RECORDS]: [
    StaffScope.PATIENTS_READ,
    StaffScope.PATIENTS_WRITE,
  ],
  [PermissionModule.REPORTING]: [StaffScope.REPORTING_READ],
  [PermissionModule.SETTINGS]: [
    StaffScope.SETTINGS_READ,
    StaffScope.SETTINGS_WRITE,
  ],
  [PermissionModule.SCHEDULING]: [
    StaffScope.SCHEDULING_READ,
    StaffScope.SCHEDULING_WRITE,
  ],
  [PermissionModule.STAFF_MANAGEMENT]: [
    StaffScope.STAFF_READ,
    StaffScope.STAFF_WRITE,
  ],
  [PermissionModule.TREATMENT_PLANS]: [
    StaffScope.TREATMENT_READ,
    StaffScope.TREATMENT_WRITE,
  ],
  [PermissionModule.DASHBOARD]: [
    StaffScope.DASHBOARD_READ,
    StaffScope.DASHBOARD_WRITE,
  ],
  [PermissionModule.MARKETING]: [
    StaffScope.MARKETING_READ,
    StaffScope.MARKETING_WRITE,
  ],
  [PermissionModule.MESSAGE]: [
    StaffScope.MESSAGING_READ,
    StaffScope.MESSAGING_WRITE,
  ],
  [PermissionModule.NOTES]: [StaffScope.NOTES_READ, StaffScope.NOTES_WRITE],
  [PermissionModule.FILES_LETTERS]: [
    StaffScope.FILES_READ,
    StaffScope.FILES_WRITE,
  ],
  [PermissionModule.FORM_BUILDER]: [
    StaffScope.FORMS_READ,
    StaffScope.FORMS_WRITE,
  ],
  [PermissionModule.IMAGE_VIEWER]: [
    StaffScope.IMAGES_READ,
    StaffScope.IMAGES_WRITE,
  ],
  [PermissionModule.LAB]: [StaffScope.LAB_READ, StaffScope.LAB_WRITE],
  [PermissionModule.NOTIFICATIONS]: [
    StaffScope.NOTIFICATIONS_READ,
    StaffScope.NOTIFICATIONS_WRITE,
  ],
}
